<script setup lang="ts">
import { AFFILIATE } from '~/constants/affiliate'
import { formatUsd, PRICES } from '~/constants/pricing'

/**
 * The public face of the affiliate program, for visitors who are not signed
 * in: what partners earn, how it works, and the way in (an account first).
 */
const { t } = useI18n()

const rate = `${Math.round(AFFILIATE.commissionRate * 100)} %`
const minPayout = formatUsd(AFFILIATE.minPayoutCents / 100)
const examplePack = formatUsd(PRICES.jobPack * AFFILIATE.commissionRate)
const exampleYearly = formatUsd(PRICES.yearly * AFFILIATE.commissionRate)

const steps = computed(() => [
  t('affiliate.how.share'),
  t('affiliate.how.buy', { days: AFFILIATE.cookieDays }),
  t('affiliate.how.earn', { rate }),
  t('affiliate.how.payout', { hold: AFFILIATE.holdDays, min: minPayout }),
])

const perks = computed(() => (['banners', 'stats', 'lifetime', 'payouts'] as const).map(id => ({
  id,
  icon: { banners: 'bi-image', stats: 'bi-graph-up', lifetime: 'bi-infinity', payouts: 'bi-wallet2' }[id],
  title: t(`affiliate.landing.perks.${id}.title`),
  text: t(`affiliate.landing.perks.${id}.text`, { days: AFFILIATE.cookieDays, min: minPayout }),
})))

const faq = computed(() => (['who', 'tracking', 'payout', 'taxes', 'rules'] as const).map(id => ({
  id: `affiliate-faq-${id}`,
  question: t(`affiliate.landing.faq.${id}.q`),
  answer: t(`affiliate.landing.faq.${id}.a`, { days: AFFILIATE.cookieDays, hold: AFFILIATE.holdDays, min: minPayout }),
})))

const { setPendingAffiliate } = usePendingPurchase()
const localize = useLocalizedPath()

/**
 * Signing up or in from here brings the visitor back to this page (in their
 * language), where joining is one click: /auth/callback and the login form
 * pick the parked path up instead of opening the CV editor.
 */
function goTo(page: '/registration' | '/login') {
  setPendingAffiliate(localize('/affiliate'))
  navigateTo({ path: localize(page), query: { reason: 'affiliate' } })
}
</script>

<template>
  <div class="py-10">
    <div class="grid items-center gap-10 lg:grid-cols-2">
      <div>
        <h2 class="!text-2xl">{{ t('affiliate.how.title') }}</h2>
        <ol class="mt-6 space-y-4">
          <li v-for="(line, index) in steps" :key="index" class="flex gap-4">
            <span class="flex size-8 shrink-0 items-center justify-center rounded-full bg-accent-soft/40 font-title font-bold text-ink dark:bg-accent/20 dark:text-accent-soft">{{ index + 1 }}</span>
            <span class="pt-1">{{ line }}</span>
          </li>
        </ol>
        <div class="mt-8 flex flex-wrap gap-3">
          <SofaxBtn :label="t('affiliate.landing.signUp')" @click="goTo('/registration')" />
          <SofaxBtn :label="t('affiliate.landing.logIn')" variant="outline" @click="goTo('/login')" />
        </div>
        <p class="mt-4 text-sm text-muted dark:text-gray-400">
          {{ t('affiliate.landing.free') }}
          <LocaleLink to="/affiliate/terms" class="link">{{ t('affiliate.termsLink') }}</LocaleLink>
        </p>
      </div>
      <div class="rounded-[0.625rem] bg-surface p-10 text-center dark:bg-gray-800">
        <div class="font-title text-7xl font-bold text-accent">{{ rate }}</div>
        <div class="mt-3 text-lg text-body dark:text-gray-300">{{ t('affiliate.how.earn', { rate }) }}</div>
        <div class="mt-6 grid grid-cols-2 gap-4 text-sm">
          <div class="rounded-lg bg-white p-4 dark:bg-gray-900">
            <div class="font-title text-2xl font-bold text-ink dark:text-white">{{ examplePack }}</div>
            <div class="mt-1 text-muted dark:text-gray-400">{{ t('affiliate.landing.examplePack') }}</div>
          </div>
          <div class="rounded-lg bg-white p-4 dark:bg-gray-900">
            <div class="font-title text-2xl font-bold text-ink dark:text-white">{{ exampleYearly }}</div>
            <div class="mt-1 text-muted dark:text-gray-400">{{ t('affiliate.landing.exampleYearly') }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
      <div v-for="perk in perks" :key="perk.id" class="rounded-[0.625rem] border border-line p-6 dark:border-gray-700">
        <i :class="['bi', perk.icon, 'text-2xl text-accent']" aria-hidden="true" />
        <h3 class="mt-3 font-title !text-base font-semibold text-ink dark:text-white">{{ perk.title }}</h3>
        <p class="mt-2 text-sm text-body dark:text-gray-300">{{ perk.text }}</p>
      </div>
    </div>

    <AffiliateProducts class="mt-16" />

    <div class="mt-16 max-w-3xl">
      <h2 class="!text-2xl">{{ t('affiliate.landing.faqTitle') }}</h2>
      <SofaxAccordion class="mt-6" :items="faq" />
    </div>
  </div>
</template>
